import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { apiInstance } from '../axiosApi';
import { decodeToken } from '../../utils/jwtUtils';

const initialState = {
  token: null,
  userDetails: null,
  userRolePermission: []
};

export const authLogin = createAsyncThunk('callex/login', async (payload) => {
  return await apiInstance.post('/auth/userlogin', payload);
});

export const authTenantLogin = createAsyncThunk('callex/tenant_admin', async (payload) => {
  return await apiInstance.post('/auth/tenant_admin', payload);
});

export const masterAdminLogin = createAsyncThunk('callex/master_admin', async (payload) => {
  return await apiInstance.post('/auth/master_admin', payload);
});

export const auditorLogin = createAsyncThunk('callex/auditor-login', async (payload) => {
  return await apiInstance.post('/auth/auditor-login', payload);
});

export const authLogout = createAsyncThunk('callex/log-out', async () => {
  return await apiInstance.post('/auth/logout', {});
});

export const authSwitchProcess = createAsyncThunk('auth/switch-process', async (payload) => {
  return await apiInstance.post('/auth/switch-process', payload);
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth: () => initialState,
    setAuthToken(state, action) {
      const decoded = decodeToken(action.payload);
      state.userDetails = {
        ...decoded,
        password: decoded?.password ?? state.userDetails?.password
      };
      state.token = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(authLogin.fulfilled, (state, action) => {
      localStorage.setItem('callex-token', JSON.stringify(action.payload.token));
      state.userDetails = {
        ...decodeToken(action.payload.token),
        password: action.meta.arg.password
      };
      state.token = action?.payload?.token;
    });
    builder.addCase(authTenantLogin.fulfilled, (state, action) => {
      localStorage.setItem('callex-token', JSON.stringify(action.payload.token));
      state.userDetails = {
        ...decodeToken(action.payload.token),
        password: action.meta.arg.password
      };
      state.userRolePermission = [...(action.payload?.data?.role_permission || [])];
      state.token = action?.payload?.token;
    });
    builder.addCase(masterAdminLogin.fulfilled, (state, action) => {
      localStorage.setItem('callex-token', JSON.stringify(action.payload.token));
      state.userDetails = decodeToken(action.payload.token);
      state.token = action?.payload?.token;
    });
    builder.addCase(auditorLogin.fulfilled, (state, action) => {
      localStorage.setItem('callex-token', JSON.stringify(action.payload.token));
      state.userDetails = {
        ...decodeToken(action.payload.token),
        password: action.meta.arg.password
      };
      state.userRolePermission = [...(action.payload?.data?.role_permission || [])];
      state.token = action?.payload?.token;
    });
    builder.addCase(authSwitchProcess.fulfilled, (state, action) => {
      localStorage.setItem('callex-token', JSON.stringify(action.payload.token));
      const decoded = decodeToken(action.payload.token);
      state.userDetails = {
        ...decoded,
        password: decoded?.password ?? state.userDetails?.password
      };
      state.token = action?.payload?.token;
    });
  }
});

export default authSlice.reducer;
export const { clearAuth, setAuthToken } = authSlice.actions;

export const selectToken = (state) => state.auth.token;
export const useToken = () => {
  const token = useSelector(selectToken);
  return useMemo(() => token, [token]);
};

export const selectUserDetails = (state) => state.auth.userDetails;
export const useUserDetails = () => {
  const userDetails = useSelector(selectUserDetails);
  return useMemo(() => userDetails, [userDetails]);
};

export const selectUserRolePermission = (state) => state.auth.userRolePermission;
export const useUserRolePermission = () => {
  const userRolePermission = useSelector(selectUserRolePermission);
  return useMemo(() => userRolePermission, [userRolePermission]);
};
